import { SplashScreen } from '@capacitor/splash-screen';
import { Route, Routes, useLocation, useRoutes } from 'react-router-dom';
import './App.css';
import 'dayjs/locale/de';
import 'dayjs/locale/ar';
import { CapacitorSwipeBackPlugin } from 'capacitor-swipe-back-plugin';

import { Suspense, useEffect, useRef } from 'react';
import './locales/i18n';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
	SelectedLanguageAtom,
	PageTitleAtom,
	LanguagesAtom,
	RoutesAtom,
	SidebarAtom,
	Snackbar,
	Loader,
	ProjtechRoutes,
	AppConfigAtom,
	Dialog,
	closeDialog,
	openDialog,
	ProjtechBtn,
} from '@projtech-package/react-components/lib';
import { RoutesData, sidebarData } from '@data';
import { APP_CONFIGS } from './config/config';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '@utils';
import { App as CapacitorApp } from '@capacitor/app';
import { getPreference, setPreference } from '@store/prefrences';
import dayjs from 'dayjs';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { subscribeToTopic, useFCM } from '@hooks/useFCM';
import { SettingsAtom } from '@store/settings';
import { LangDropdownComponent } from '@components/lang-dropdown';
import { PushNotifications } from '@capacitor/push-notifications';
import { useProjtechNavigate } from '@hooks/useNavigate';
import SwipeBackHandler from '@components/go-back';
import { useNetworkStatus } from '@hooks/useCheckNetwork';
import { ToastBar } from '@components/toast-bar';
// import { useIOSSwipeBack } from '@hooks/useSwipeBack';
import { AnimatedRoutes } from '@components/swipe-page';
import { useHandleCache } from '@hooks/useHandleCache';

function App() {
	const { t } = useTranslation();
	const audioRef = useRef(null);
	const content = useRoutes(ProjtechRoutes(RoutesData));
	const setPageTitle = useSetAtom(PageTitleAtom);
	const [languages, setLanguages] = useAtom(LanguagesAtom);
	const handleCache = useHandleCache();
	const setRoutes = useSetAtom(RoutesAtom);
	const setSidebar = useSetAtom(SidebarAtom);
	const langQuery = new URLSearchParams(window.location.search).get('local');
	const [lang, setLang] = useAtom(SelectedLanguageAtom);
	const setAppConfig = useSetAtom(AppConfigAtom);
	const setSettingsAtom = useSetAtom(SettingsAtom);
	const settings = useAtomValue(SettingsAtom);
	const { router } = useProjtechNavigate();
	const isOnline = useNetworkStatus();

	const { i18n } = useTranslation();
	useEffect(() => {
		if (i18n.resolvedLanguage) {
			document.documentElement.dir = i18n.dir(i18n.resolvedLanguage);
		}
	}, [i18n.resolvedLanguage]);

	useEffect(() => {
		CapacitorApp.addListener('backButton', ({ canGoBack }) => {
			if (!canGoBack) {
				CapacitorApp.exitApp();
			} else {
				window.history.back();
			}
		});
	}, []);

	// useEffect(() => {
	// 	const hideSplashScreen = async () => {
	// 		await SplashScreen.show({ autoHide: false });

	// 		setTimeout(async () => {
	// 			await SplashScreen.hide();
	// 		}, 2000);
	// 	};

	// 	hideSplashScreen();
	// }, []);

	useEffect(() => {
		const sendHeight = () => {
			// get the height of the content subscribe-learn-quran and send it to the parent
			const contentElement = document.getElementById('subscribe-learn-quran');
			if (contentElement) {
				const height = contentElement.offsetHeight;

				window.parent.postMessage({ type: 'resize', height, bodyHeight: document.body.scrollHeight }, '*');
			}
			// get the height of the content subscribe-learn-quran and send it to the parent every 500ms for 5 seconds
			setTimeout(() => {
				const contentElement = document.getElementById('subscribe-learn-quran');
				if (contentElement) {
					const height = contentElement.offsetHeight;
					window.parent.postMessage({ type: 'resize', height, bodyHeight: document.body.scrollHeight }, '*');
				}
			}, 500);
		};

		sendHeight();
		// Bei Größenänderungen (z.B. Akkordeons, dynamische Inhalte)
		const observer = new ResizeObserver(sendHeight);
		observer.observe(document.body);

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const setInitialLanguage = async () => {
			const langPreference = await handleCache.getCache('lang');
			if (!langPreference) {
				openDialog('open-lang-dialog');
				const langToSet = langQuery || langPreference || '';
				i18n.changeLanguage(langToSet);
				if (langToSet) {
					await handleCache.setCache('lang', langToSet);
				}
				document.documentElement.lang = langToSet;
				document.documentElement.dir = langToSet === 'ar' ? 'rtl' : 'ltr';
				setLang(langToSet);
			} else {
				const langToSet = langQuery || langPreference || '';
				i18n.changeLanguage(langToSet);
				await handleCache.setCache('lang', langToSet);
				document.documentElement.lang = langToSet;
				document.documentElement.dir = langToSet === 'ar' ? 'rtl' : 'ltr';
				setLang(langToSet);
			}
		};

		setInitialLanguage();
		setPageTitle(Object.entries(RoutesData).find((f) => f[1].route === window.location.pathname)?.[1].title || '');
	}, [setLang, setPageTitle, lang, langQuery]);

	useEffect(() => {
		// @ts-ignore
		setAppConfig(APP_CONFIGS);
		setLanguages(APP_CONFIGS.LANGUAGES);
		// @ts-ignore
		setRoutes(RoutesData);
		setSidebar(sidebarData);
	}, [setAppConfig, setLanguages, setRoutes, setSidebar]);

	useEffect(() => {
		dayjs.locale(lang);
	}, [lang]);

	useEffect(() => {
		if (Capacitor.getPlatform() !== 'web') {
			SafeArea.getSafeAreaInsets().then(({ insets }) => {
				document.body.style.setProperty('--ion-safe-area-top', `${insets.top - 6}px`);
				document.body.style.setProperty('--ion-safe-area-bottom', `${insets.bottom}px`);
			});
		}
	}, []);

	useEffect(() => {
		CapacitorApp.addListener('appUrlOpen', (event) => {
			const url = new URL(event.url);
			const path = url.pathname + url.search;
			window.history.replaceState({}, '', path);

			if (event.url.startsWith('myapp://de/action')) {
				router(RoutesData.success.route, RoutesData.success.title);
			}
		});
	}, []);

	const fetchPrayerSettings = async () => {
		if (Capacitor.getPlatform() === 'web') return;

		try {
			const data = await getPreference('notificationSettings');
			if (!data) {
				await setPreference({
					key: 'notificationSettings',
					value: {
						prayerTimesSettings: {
							Fajr: false,
							IqamatFajr: false,
							Sonnenaufgang: false,
							Dhuhr: false,
							Asr: false,
							Maghrib: false,
							Isha: false,
						},
						notifications: {
							duaa: false,
							lessons: false,
							notification: false,
							quran: false,
						},
					},
				});
				setSettingsAtom({
					prayerTimesSettings: {
						Fajr: false,
						IqamatFajr: false,
						Sonnenaufgang: false,
						Dhuhr: false,
						Asr: false,
						Maghrib: false,
						Isha: false,
					},
					notifications: {
						duaa: false,
						lessons: false,
						notification: false,
						quran: false,
						azan: false,
					},
				});

				return;
			} else {
				setSettingsAtom({
					prayerTimesSettings: {
						Fajr: data.prayerTimesSettings.Fajr || false,
						IqamatFajr: data.prayerTimesSettings.IqamatFajr || false,
						Sonnenaufgang: data.prayerTimesSettings.Sonnenaufgang || false,
						Dhuhr: data.prayerTimesSettings.Dhuhr || false,
						Asr: data.prayerTimesSettings.Asr || false,
						Maghrib: data.prayerTimesSettings.Maghrib || false,
						Isha: data.prayerTimesSettings.Isha || false,
					},
					notifications: {
						duaa: data.notifications.duaa,
						lessons: data.notifications.lessons,
						notification: data.notifications.notification,
						quran: data.notifications.quran,
						azan: data.notifications.azan,
					},
				});
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const hiddenPaths = ['/', '/home', 'quran'];
		if (Capacitor.getPlatform() === 'ios') {
			if (hiddenPaths.some((p) => window.location.pathname === p)) {
				CapacitorSwipeBackPlugin.disable();
			} else {
				CapacitorSwipeBackPlugin.enable();
			}
		}
	}, [content]);

	useEffect(() => {
		fetchPrayerSettings();
	}, []);

	useFCM(audioRef, settings);

	if (!isOnline) {
		return (
			<div className="w-full h-screen flex flex-col justify-center items-center gap-5">
				<img src="/assets/no-internet.jpg" alt="No Internet" className="object-cover" />
				<div className="text-2xl font-bold text-red-500">{t('Keine Internetverbindung')}</div>
				<div className="text-lg text-gray-500 text-center">
					{t('Bitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.')}
				</div>
				<ProjtechBtn
					onClick={() => {
						window.location.reload();
					}}
					text={t('Erneut versuchen')}
				/>
			</div>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			{/* <SwipeBackHandler /> */}
			{!lang && (
				<Dialog
					id="open-lang-dialog"
					content={
						<div className="text-black w-full flex justify-center">
							<LangDropdownComponent
								onSubmit={async () => {
									await Object.keys({ ...settings.notifications, ...settings.prayerTimesSettings }).forEach(
										async (key) => {
											await subscribeToTopic(key);
										},
									);
								}}
							/>
						</div>
					}
					onCLose={async () => {
						await handleCache.setCache('lang', APP_CONFIGS.LANGUAGES[0]?.code);
						setLang(APP_CONFIGS.LANGUAGES[0]?.code);
						dayjs.locale(APP_CONFIGS.LANGUAGES[0]?.code);
						i18n.changeLanguage(APP_CONFIGS.LANGUAGES[0]?.code);
						await Object.keys({ ...settings.notifications, ...settings.prayerTimesSettings }).forEach(async (key) => {
							await subscribeToTopic(key);
						});
					}}
				/>
			)}
			{/* <AnimatedRoutes routes={ProjtechRoutes(RoutesData)} /> */}

			{content}
			<ToastBar />
			{/* <SwipeBack /> */}

			<audio ref={audioRef} src="/assets/azzan.mp3" preload="auto" />
		</QueryClientProvider>
	);
}

export default App;
